import React, { useState, useEffect, useCallback } from "react";
import { Save, LogIn, LogOut, MapPin } from "lucide-react";
import { useEntitySettings } from "../../../contexts/EntitySettingsContext";
import FormControl from "../../common/FormControl";
import Button from "../../common/Button";
import Skeleton from "../../common/Skeleton";
import NoDataFound from "../../common/NoDataFound";
import Toast, { type ToastMessage } from "../../common/Toast";
import { confirmWarning } from "../../common/confirm";
// self-service: no RBAC gate — any logged-in user may record their own attendance
import { upsertDay, listPunches, punch } from "../../../services/hrService";
import { AttendancePunch } from "../../../services/hrTypes";

// Values MUST match backend ATTENDANCE_STATUS_VALUES (hr_schemas.py): {"Present","WFH","On-Leave","Absent"}
const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: "Present", label: "Present" },
    { value: "WFH", label: "Work From Home" },
    { value: "On-Leave", label: "On Leave" },
    { value: "Absent", label: "Absent" },
];

// FastAPI 422 returns detail as an array of objects — never render it as a React child.
const errMsg = (err: any, fallback: string): string => {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join("; ") || fallback;
    return err?.message || fallback;
};

const today = () => new Date().toISOString().split("T")[0];

const getGeo = (): Promise<{ latitude?: number; longitude?: number }> =>
    new Promise((resolve) => {
        if (!("geolocation" in navigator)) return resolve({});
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
            () => resolve({}),
            { timeout: 5000 },
        );
    });

const MyAttendance: React.FC = () => {
    const { formatDate } = useEntitySettings();
    const [attendanceDate, setAttendanceDate] = useState(today());
    const [status, setStatus] = useState("Present");
    const [remarks, setRemarks] = useState("");
    const [savingDay, setSavingDay] = useState(false);
    const [punching, setPunching] = useState<"IN" | "OUT" | null>(null);

    const [punches, setPunches] = useState<AttendancePunch[]>([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const fetchPunches = useCallback(async () => {
        setLoading(true);
        try {
            setPunches(await listPunches(attendanceDate));
        } catch (err: any) {
            setToast({ type: "error", message: errMsg(err, "Failed to load punches") });
        } finally {
            setLoading(false);
        }
    }, [attendanceDate]);

    useEffect(() => { fetchPunches(); }, [fetchPunches]);

    const handleMarkDay = async () => {
        setSavingDay(true);
        try {
            await upsertDay({ attendance_date: attendanceDate, status, remarks: remarks || undefined });
            setToast({ type: "success", message: "Marked as " + status });
        } catch (err: any) {
            setToast({ type: "error", message: errMsg(err, "Failed to mark day") });
        } finally {
            setSavingDay(false);
        }
    };

    const handlePunch = async (punch_type: "IN" | "OUT") => {
        // Ask for confirmation when punching out
        if (punch_type === "OUT") {
            const confirmed = await confirmWarning({
                title: "Punch Out?",
                text: "Are you sure you want to punch out?",
                confirmText: "Yes, Punch Out"
            });
            if (!confirmed) return;
        }

        setPunching(punch_type);
        try {
            const geo = await getGeo();
            // backend PUNCH_TYPES = {'login','logoff'} — map UI IN/OUT to those
            await punch({ punch_type: punch_type === "IN" ? "login" : "logoff", ...geo });
            setToast({ type: "success", message: "Punched " + punch_type });
            fetchPunches();
        } catch (err: any) {
            setToast({ type: "error", message: errMsg(err, "Punch failed") });
        } finally {
            setPunching(null);
        }
    };

    const fmtTime = (iso: string) => {
        try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
        catch { return iso; }
    };

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <span style={{ fontWeight: 600, fontSize: "0.9375rem" }}>My Attendance — {formatDate(attendanceDate)}</span>
            </div>

            <div className="netbody-content" style={{ overflowY: "auto" }}>
                {/* Day-mark */}
                <div style={{ padding: "1rem", border: "1px solid var(--border-subtle)", borderRadius: 10, marginBottom: "1rem" }}>
                    <div style={{ fontWeight: 600, marginBottom: 12, fontSize: "0.875rem" }}>Mark attendance</div>
                    <div className="flex items-end gap-3" style={{ flexWrap: "nowrap", marginBottom: "1rem" }}>
                        <div style={{ minWidth: 180 }}>
                            <FormControl.DatePicker
                                label="Date"
                                value={attendanceDate}
                                onChange={(e) => setAttendanceDate(e.target.value)}
                                data-testid="INP_attendance_date"
                            />
                        </div>
                        <div style={{ minWidth: 200, flex: 0 }}>
                            <FormControl.SearchSelect
                                label="Status"
                                options={STATUS_OPTIONS}
                                value={status}
                                onChange={(val) => setStatus(val as string)}
                                placeholder="Select status…"
                                data-testid="SEL_attendance_status"
                            />
                        </div>
                    </div>
                    <div className="flex items-end gap-3" style={{ flexWrap: "nowrap" }}>
                        <div style={{ flex: 1, minWidth: 220 }}>
                            <FormControl.Input
                                label="Remarks"
                                type="text"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="Optional note"
                                data-testid="INP_attendance_remarks"
                            />
                        </div>
                        <Button
                            variant="fill"
                            icon={Save}
                            disabled={savingDay}
                            onClick={handleMarkDay}
                            data-testid="BTN_attendance_mark_day"
                        >
                            {savingDay ? "Saving..." : "Mark"}
                        </Button>
                    </div>
                </div>

                {/* Punches */}
                <div style={{ padding: "1rem", border: "1px solid var(--border-subtle)", borderRadius: 10 }}>
                    <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>Punches</div>
                        <div className="flex gap-2">
                            <Button
                                variant="success"
                                icon={LogIn}
                                disabled={punching !== null}
                                onClick={() => handlePunch("IN")}
                                data-testid="BTN_attendance_punch_in"
                            >
                                {punching === "IN" ? "Punching..." : "Punch In"}
                            </Button>
                            <Button
                                variant="danger"
                                icon={LogOut}
                                disabled={punching !== null}
                                onClick={() => handlePunch("OUT")}
                                data-testid="BTN_attendance_punch_out"
                            >
                                {punching === "OUT" ? "Punching..." : "Punch Out"}
                            </Button>
                        </div>
                    </div>

                    <div className="table-list">
                        <table className="table-list-table">
                            <thead>
                                <tr>
                                    <th className="table-list-th table-list-cell--sno">S.No</th>
                                    <th className="table-list-th">Type</th>
                                    <th className="table-list-th">Time</th>
                                    <th className="table-list-th">Location</th>
                                </tr>
                            </thead>
                            <tbody aria-busy={loading} aria-live="polite">
                                {loading && <Skeleton.TableRows cols={["3xs", "pill", "sm", "md"]} />}
                                {!loading && punches.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="table-list-cell">
                                            <NoDataFound title="No punches yet" message="Punch in to start your day." />
                                        </td>
                                    </tr>
                                )}
                                {!loading && punches.map((p, idx) => (
                                    <tr key={p.id} className="table-list-row" data-testid={`ROW_attendance_punch_${p.id}`}>
                                        <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                                        <td className="table-list-cell">
                                            <span className={`status-badge xs ${p.punch_type === "login" ? "success" : "warning"}`}>
                                                {p.punch_type === "login" ? "IN" : "OUT"}
                                            </span>
                                        </td>
                                        <td className="table-list-cell">{fmtTime(p.punched_at)}</td>
                                        <td className="table-list-cell">
                                            {p.location_label || (p.latitude != null
                                                ? <span className="flex items-center gap-1"><MapPin size={12} />{p.latitude.toFixed(3)}, {p.longitude?.toFixed(3)}</span>
                                                : "-")}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default MyAttendance;
